import React, { useCallback, useEffect, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Trash2,
  Rocket,
  Calendar,
  Search,
  Check,
  X,
  Send,
  AlertTriangle,
  Loader2,
  Pencil,
  Save,
  Tag,
  ChevronRight,
  ArrowRight,
} from "lucide-react";

// Same palette used in ProductionDashboard.jsx for the per-staff color tag.
const COLOR_PALETTE = [
  { hex: "#ef4444", name: "Rosso" },
  { hex: "#f97316", name: "Arancio" },
  { hex: "#eab308", name: "Giallo" },
  { hex: "#22c55e", name: "Verde" },
  { hex: "#06b6d4", name: "Ciano" },
  { hex: "#3b82f6", name: "Blu" },
  { hex: "#a855f7", name: "Viola" },
  { hex: "#ec4899", name: "Rosa" },
];

function prettyDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toLocalInput(date) {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultPublishWhen() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 10);
  return d;
}

export default function CodaMetricoolFoto() {
  const [posts, setPosts] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("queue"); // 'queue' = da pubblicare; 'published'
  const [loading, setLoading] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [republishMode, setRepublishMode] = useState(false);
  // Per-session collapse map. ALL clients are collapsed by default.
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  // Local cache so the chip flips immediately on pick.
  const [colorTags, setColorTags] = useState({}); // { clientId: "#hex" | null }

  const toggleClient = useCallback((clientId) => {
    if (!clientId) return;
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  const setClientColor = useCallback(async (clientId, color) => {
    if (!clientId) return;
    setColorTags((prev) => ({ ...prev, [clientId]: color }));
    try {
      await api.put(`/staff/clients/${clientId}/color-tag`, { color });
    } catch (e) {
      toast.error(apiError(e));
      setColorTags((prev) => ({ ...prev, [clientId]: null }));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/posts", {
        params: { media_kind: "foto" },
      });
      const items = data.items || [];
      setPosts(items);
      // Hydrate colour tags from API.
      setColorTags((prev) => {
        const next = { ...prev };
        for (const it of items) {
          if (it.client_id && !(it.client_id in next)) {
            next[it.client_id] = it.client_color || null;
          }
        }
        return next;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clients = Array.from(
    new Map(
      posts.map((p) => [p.client_id, { id: p.client_id, name: p.client_name }])
    ).values()
  );

  const filtered = posts.filter((p) => {
    // in_rubrica: post enrolled in the rubrica via "Solo rubrica" mode → hide from both tabs.
    if (p.status === "in_rubrica") return false;
    // Tab filter: queue = not yet published; published = sent/scheduled to Metricool
    const isPublished = p.status === "published" || p.status === "scheduled";
    if (tab === "queue" && isPublished) return false;
    if (tab === "published" && !isPublished) return false;
    if (clientFilter && p.client_id !== clientFilter) return false;
    if (search) {
      const needle = search.toLowerCase();
      if (
        !(p.title || "").toLowerCase().includes(needle) &&
        !(p.caption || "").toLowerCase().includes(needle) &&
        !(p.client_name || "").toLowerCase().includes(needle)
      )
        return false;
    }
    return true;
  });

  const queueCount = posts.filter(
    (p) =>
      p.status !== "published" &&
      p.status !== "scheduled" &&
      p.status !== "in_rubrica"
  ).length;
  const publishedCount = posts.filter(
    (p) => p.status === "published" || p.status === "scheduled"
  ).length;

  const groupedByClient = filtered.reduce((acc, p) => {
    const key = p.client_id || "unknown";
    if (!acc[key]) acc[key] = { name: p.client_name, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});
  // Pubblicati: ordina per scheduled_at DESC (più recente in alto, es. 08/05
  // sopra il 07/05). Queue mantiene l'ordine ASC originale dal backend.
  if (tab === "published") {
    Object.values(groupedByClient).forEach((g) => {
      g.items.sort((a, b) => {
        const da = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
        const db = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
        return db - da;
      });
    });
  }

  const removePost = async (id) => {
    if (!window.confirm("Eliminare questa bozza?")) return;
    try {
      await api.delete(`/staff/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Bozza eliminata");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const moveToVideo = async (id) => {
    if (
      !window.confirm(
        "Spostare questa bozza in Coda Video?\n\n" +
          "Il post sparirà da Coda Foto e comparirà in Coda Video con tutti i contenuti (titolo, caption, hashtag) invariati."
      )
    )
      return;
    try {
      await api.patch(`/staff/posts/${id}`, { media_kind: "video" });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Bozza spostata in Coda Video ✓");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="metricool-foto-queue-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Schedulazione
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Coda Foto
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Bozze foto pronte per la pubblicazione e archivio delle foto già pubblicate.
          Per le foto puoi pubblicare subito su Facebook/Instagram, programmare per una data
          futura, oppure mettere in Rubrica Foto (Google Business Profile).
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 rounded-full bg-white border border-[var(--mp-border)] p-1 w-fit">
        <button
          type="button"
          data-testid="queue-foto-tab-queue"
          onClick={() => setTab("queue")}
          className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${
            tab === "queue"
              ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
              : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
          }`}
        >
          Da pubblicare · {queueCount}
        </button>
        <button
          type="button"
          data-testid="queue-foto-tab-published"
          onClick={() => setTab("published")}
          className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${
            tab === "published"
              ? "bg-[var(--mp-ink)] text-white"
              : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
          }`}
        >
          Pubblicati · {publishedCount}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mp-muted)]" />
          <Input
            data-testid="queue-foto-search"
            placeholder="Cerca per titolo, caption o cliente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-full border-[var(--mp-border)] h-10"
          />
        </div>
        <select
          data-testid="queue-foto-client-filter"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="h-10 px-3 rounded-full border border-[var(--mp-border)] bg-white text-sm"
        >
          <option value="">Tutti i clienti</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || "—"}
            </option>
          ))}
        </select>
      </div>

      {loading && posts.length === 0 ? (
        <div className="text-center py-20 text-[var(--mp-muted)]">Caricamento…</div>
      ) : Object.keys(groupedByClient).length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-12 text-center">
          <Rocket className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-display text-xl">
            {tab === "published"
              ? "Nessun post pubblicato ancora"
              : "Nessuna bozza in coda"}
          </div>
          <div className="text-sm text-[var(--mp-muted)] mt-1">
            {tab === "published"
              ? "I post inviati a Metricool appariranno qui."
              : 'Prepara un post dalla Produzione cliccando sul pulsante "Prepara".'}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByClient).map(([cid, group]) => {
            const isExpanded = expandedClients.has(cid);
            const tagColor = colorTags[cid] || null;
            return (
            <section
              key={cid}
              data-testid={`queue-group-${cid}`}
              className="rounded-3xl border border-[var(--mp-ink)] bg-white overflow-hidden"
            >
              <div className="px-5 py-3 flex items-center justify-between bg-[var(--mp-dash-bg)] border-b border-[var(--mp-border)]">
                <div className="flex items-center gap-2.5 min-w-0">
                  <button
                    type="button"
                    onClick={() => toggleClient(cid)}
                    data-testid={`queue-client-toggle-${cid}`}
                    title={isExpanded ? "Comprimi cliente" : "Espandi cliente"}
                    className="h-8 w-8 rounded-full grid place-items-center hover:bg-white/60 transition-colors shrink-0"
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  <ClientColorChip
                    clientId={cid}
                    color={tagColor}
                    onChange={(c) => setClientColor(cid, c)}
                  />
                  <button
                    type="button"
                    onClick={() => toggleClient(cid)}
                    className="text-left min-w-0"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                      Cliente
                    </div>
                    <div className="font-display text-lg tracking-tight truncate">
                      {group.name || "—"}
                    </div>
                  </button>
                </div>
                <Badge className="rounded-full bg-[var(--mp-ink)] text-white text-[10px] uppercase tracking-[0.18em] shrink-0">
                  {group.items.length} {group.items.length === 1 ? "bozza" : "bozze"}
                </Badge>
              </div>
              {isExpanded && (
                <div className="divide-y divide-[var(--mp-border)]">
                  {group.items.map((p) => (
                    <PostRow
                      key={p.id}
                      post={p}
                      onOpen={() => {
                        setRepublishMode(false);
                        setOpenPost(p);
                      }}
                      onRepublish={() => {
                        setRepublishMode(true);
                        setOpenPost(p);
                      }}
                      onDelete={() => removePost(p.id)}
                      onMoveToVideo={() => moveToVideo(p.id)}
                    />
                  ))}
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}

      <MetricoolPublishDialog
        post={openPost}
        republishMode={republishMode}
        onClose={() => {
          setOpenPost(null);
          setRepublishMode(false);
        }}
        onUpdated={(updated) => {
          setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setOpenPost(updated);
        }}
        onPublished={(updated) => {
          setOpenPost(null);
          setRepublishMode(false);
          setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          load();
        }}
      />
    </div>
  );
}

function PostRow({ post, onOpen, onRepublish, onDelete, onMoveToVideo }) {
  const isPublished = post.status === "published" || post.status === "scheduled";
  return (
    <div
      data-testid={`queue-row-${post.id}`}
      className="p-4 sm:p-5 grid sm:grid-cols-[96px_1fr_180px_auto] gap-3 items-start hover:bg-[var(--mp-dash-bg)]/60 transition-colors cursor-pointer"
      onClick={onOpen}
      role="button"
    >
      <div className="h-20 w-20 rounded-xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] grid place-items-center overflow-hidden">
        {post.media_url ? (
          <MediaPreview url={post.media_url} />
        ) : (
          <div className="text-[10px] text-[var(--mp-muted)] px-1 text-center">
            Nessun media
          </div>
        )}
      </div>
      <div className="min-w-0">
        {post.title && (
          <div className="font-display text-base tracking-tight truncate">
            {post.title}
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap line-clamp-3 mt-0.5">
          {post.caption}
        </div>
        {post.hashtags?.length > 0 && (
          <div className="mt-2 text-[11px] text-[var(--mp-muted)] truncate">
            {post.hashtags.join(" ")}
          </div>
        )}
      </div>
      <div className="text-sm flex items-center gap-1.5 text-[var(--mp-muted)]">
        <Calendar className="h-3.5 w-3.5" />
        {prettyDate(post.scheduled_at)}
      </div>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {isPublished ? (
          <>
            <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] uppercase tracking-[0.18em]">
              <Check className="h-3 w-3 mr-1" />
              {post.status === "published" ? "Pubblicato" : "Programmato"}
            </Badge>
            <Button
              size="sm"
              onClick={onRepublish}
              data-testid={`queue-republish-${post.id}`}
              title="Ripubblica questo post"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-8 text-xs"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Ripubblica
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={onOpen}
            data-testid={`queue-open-${post.id}`}
            title="Apri e pubblica"
            className="rounded-full bg-[var(--mp-yellow)] hover:bg-amber-300 text-[var(--mp-ink)] border border-[var(--mp-ink)] h-8 text-xs"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Apri
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onMoveToVideo && onMoveToVideo(); }}
          data-testid={`queue-move-to-video-${post.id}`}
          className="rounded-full border-[var(--mp-ink)] h-8 text-xs"
          title="Sposta questa bozza in Coda Video (se è finita per errore in Coda Foto)"
        >
          <ArrowRight className="h-3.5 w-3.5 mr-1" />
          In Coda Video
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          data-testid={`queue-delete-${post.id}`}
          className="h-8 w-8 p-0 text-[var(--mp-primary)]"
          title="Rimuovi"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function MediaPreview({ url }) {
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  if (isVideo) {
    return (
      <video
        src={full}
        className="h-full w-full object-cover"
        muted
        preload="metadata"
      />
    );
  }
  return <img src={full} alt="media" className="h-full w-full object-cover" />;
}

function MetricoolPublishDialog({ post, republishMode = false, onClose, onUpdated, onPublished }) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [providers, setProviders] = useState({
    instagram: true,
    facebook: true,
    gmb: true,
  });
  // 3-way mode: "publish_and_recycle" | "publish_only" | "schedule_via_rubrica" (default)
  // Default lands on "schedule_via_rubrica" because the agency's main
  // workflow is now: feed the rubrica queue first, let the cron publish
  // on schedule. Hitting "publish now" by accident on every post would
  // bypass the rubrica completely.
  const [rubricaMode, setRubricaMode] = useState("schedule_via_rubrica");
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!post) return;
    setTitle(post.title || "");
    setCaption(post.caption || "");
    setHashtags((post.hashtags || []).join(" "));
    // Republish: always reset to default (now+10min). View existing post: keep its date.
    if (republishMode) {
      setScheduleAt(toLocalInput(defaultPublishWhen()));
    } else if (post.scheduled_at) {
      try {
        setScheduleAt(toLocalInput(new Date(post.scheduled_at)));
      } catch {
        setScheduleAt(toLocalInput(defaultPublishWhen()));
      }
    } else {
      setScheduleAt(toLocalInput(defaultPublishWhen()));
    }
    setProviders({ instagram: true, facebook: true, gmb: true });
    setRubricaMode("schedule_via_rubrica");
    // We intentionally only re-run this effect when the post identity or
    // republish toggle change — re-running on every prop ref would clobber
    // edits while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, republishMode]);

  if (!post) return null;

  // Republish mode unlocks editing/publishing even if status is published/scheduled.
  const isLocked = !republishMode && (post.status === "published" || post.status === "scheduled");

  const togglerProvider = (key) => () =>
    setProviders((prev) => ({ ...prev, [key]: !prev[key] }));

  const buildTagArr = () =>
    hashtags
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith("#") ? t : "#" + t))
      .slice(0, 5);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const body = {
        title: title.trim(),
        caption,
        hashtags: buildTagArr(),
        scheduled_at: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      };
      const { data } = await api.patch(`/staff/posts/${post.id}`, body);
      toast.success("Bozza aggiornata");
      onUpdated && onUpdated(data.post);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingDraft(false);
    }
  };

  const publish = async () => {
    const list = Object.entries(providers)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (list.length === 0) {
      toast.error("Seleziona almeno una piattaforma");
      return;
    }
    setPublishing(true);
    try {
      // Save current edits first
      await api.patch(`/staff/posts/${post.id}`, {
        title: title.trim(),
        caption,
        hashtags: buildTagArr(),
        scheduled_at: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      });
      // Then publish — auto_publish is now ALWAYS true (UI checkbox removed
      // because staff kept forgetting to tick it; the only mode the agency
      // ever uses is "submit & let Metricool fan out").
      const body = {
        providers: list,
        auto_publish: true,
        rubrica_mode: rubricaMode,
        schedule_date: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      };
      const { data } = await api.post(
        `/staff/posts/${post.id}/publish-metricool`,
        body
      );
      toast.success("Post inviato a Metricool — pubblicazione in corso");
      onPublished && onPublished(data.post);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="metricool-foto-publish-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Send className="h-5 w-5" /> {republishMode ? "Ripubblica su Metricool" : isLocked ? "Bozza pubblicata" : "Pubblica su Metricool"}
          </DialogTitle>
          <DialogDescription>
            Cliente: <strong>{post.client_name}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Video / media preview */}
        {post.media_url && (
          <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-2 flex items-center justify-center min-h-[180px]">
            <MediaLargePreview url={post.media_url} />
          </div>
        )}

        {/* Title */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Titolo
          </label>
          <Input
            data-testid="pub-foto-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isLocked}
            placeholder="Titolo del post"
            maxLength={60}
            className="rounded-full border-[var(--mp-border)] h-10"
          />
        </div>

        {/* Caption */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Caption
          </label>
          <Textarea
            data-testid="pub-foto-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={isLocked}
            className="min-h-[120px] rounded-2xl border-[var(--mp-border)]"
          />
        </div>

        {/* Hashtags */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
            <Tag className="h-3 w-3" /> Hashtag (max 5, separati da spazio)
          </label>
          <Textarea
            data-testid="pub-foto-hashtags"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            disabled={isLocked}
            className="min-h-[60px] rounded-2xl border-[var(--mp-border)] font-mono text-sm"
          />
        </div>

        {/* Schedule */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Data e ora pubblicazione
          </label>
          <Input
            type="datetime-local"
            data-testid="pub-foto-when"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            disabled={isLocked}
            className="rounded-full border-[var(--mp-border)] h-10"
          />
          <div className="mt-1 text-[11px] text-[var(--mp-muted)]">
            Default: tra 10 minuti. Modificalo per programmare a una data futura.
          </div>
        </div>

        {!isLocked && (
          <>
            {/* Providers */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-2 block">
                Piattaforme
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "instagram", label: "Instagram" },
                  { k: "facebook", label: "Facebook" },
                  { k: "gmb", label: "Google Business" },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={togglerProvider(k)}
                    data-testid={`pub-provider-${k}`}
                    className={`h-9 px-4 rounded-full border text-sm font-medium transition-colors ${
                      providers[k]
                        ? "bg-[var(--mp-ink)] border-[var(--mp-ink)] text-white"
                        : "bg-white border-[var(--mp-border)] text-[var(--mp-muted)] hover:border-[var(--mp-ink)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 3-way mode selector */}
            <div className="rounded-2xl border-2 border-[var(--mp-ink)] bg-white p-3 space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Modalità di uscita
              </div>
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "publish_and_recycle"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-foto-mode-recycle"
                  value="publish_and_recycle"
                  checked={rubricaMode === "publish_and_recycle"}
                  onChange={() => setRubricaMode("publish_and_recycle")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>Pubblica adesso + metti in fondo alla Rubrica Foto</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    (esce subito sui social, su GMB viene riciclata dopo le altre)
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "publish_only"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-foto-mode-only"
                  value="publish_only"
                  checked={rubricaMode === "publish_only"}
                  onChange={() => setRubricaMode("publish_only")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>Pubblica adesso (o alla data scelta), NIENTE rubrica</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    (one-shot — usa la data nel campo sopra per programmare nel futuro)
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "schedule_via_rubrica"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-foto-mode-rubrica"
                  value="schedule_via_rubrica"
                  checked={rubricaMode === "schedule_via_rubrica"}
                  onChange={() => setRubricaMode("schedule_via_rubrica")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>📸 Solo Rubrica Foto (testa)</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    non pubblica subito; uscirà su Google Business al prossimo slot della Rubrica Foto
                  </span>
                </span>
              </label>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Verifica che il salone sia collegato a un brand Metricool dalla Rubrica
                cliente.
              </span>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)] flex-wrap">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={savingDraft || publishing}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="pub-foto-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
          {!isLocked && (
            <>
              <Button
                onClick={saveDraft}
                disabled={savingDraft || publishing}
                variant="outline"
                data-testid="pub-foto-save-draft"
                className="rounded-full border-[var(--mp-ink)]"
              >
                {savingDraft ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                {savingDraft ? "Salvo…" : "Salva bozza"}
              </Button>
              <Button
                onClick={publish}
                disabled={publishing || savingDraft}
                data-testid="pub-foto-confirm"
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                {publishing
                  ? "Invio…"
                  : republishMode
                  ? "Ripubblica"
                  : rubricaMode === "schedule_via_rubrica"
                  ? "Metti in rubrica"
                  : "Pubblica"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MediaLargePreview({ url }) {
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  if (isVideo) {
    return (
      <video
        src={full}
        className="max-h-[280px] w-auto rounded-xl"
        controls
        muted
        preload="metadata"
      />
    );
  }
  return (
    <img
      src={full}
      alt="anteprima"
      className="max-h-[280px] w-auto rounded-xl"
    />
  );
}


function ClientColorChip({ clientId, color, onChange }) {
  const [open, setOpen] = useState(false);
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest?.(`[data-color-chip="${clientId}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, clientId]);

  const dotStyle = color ? { background: color } : {};
  return (
    <div
      data-color-chip={clientId}
      className="relative shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        data-testid={`queue-client-color-${clientId}`}
        title="Cambia il colore di questo cliente"
        className={`h-7 w-7 rounded-full border-2 border-[var(--mp-ink)] transition-transform hover:scale-105 ${
          color ? "" : "bg-white"
        }`}
        style={dotStyle}
      />
      {open && (
        <div
          data-testid={`queue-client-color-popover-${clientId}`}
          className="absolute left-0 top-9 z-50 rounded-2xl border-2 border-[var(--mp-ink)] bg-white p-2 shadow-lg flex items-center gap-1.5"
        >
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.hex}
              type="button"
              onClick={() => {
                onChange(c.hex);
                setOpen(false);
              }}
              title={c.name}
              data-testid={`queue-client-color-pick-${clientId}-${c.hex.replace("#", "")}`}
              className={`h-6 w-6 rounded-full border-2 hover:scale-110 transition-transform ${
                color === c.hex ? "border-[var(--mp-ink)]" : "border-transparent"
              }`}
              style={{ background: c.hex }}
            />
          ))}
          {color && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              title="Rimuovi colore"
              data-testid={`queue-client-color-clear-${clientId}`}
              className="h-6 w-6 rounded-full border-2 border-[var(--mp-border)] bg-white grid place-items-center hover:bg-stone-50"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
